//@ts-ignore
import EnchancedSelect, { components, InputActionMeta } from "react-select";
import { FormContext, iChoice, iControlProps } from "../types";

import { Container } from ".";
import React, { useEffect, useState } from "react";
import { SelectCustomStyles } from "./select/styles";
import { evaluates_as_hiddenV2 } from "../conditional-logic";
import { replaceMergeTagsInString, stringHasValueTag } from "../helper";
import { isMobile } from "../../theme/style-utils-coras-nua";

type SelectValue = {
    label: string;
    value?: string;
    options?: { label: string, value: string }[];
    customProps?: any;
};

export type Props = iControlProps & {
    choices: iChoice[];
    id?: string;
    cssClass?: string;
    menuIsOpen?: boolean;
    classNamePrefix?: string;
    isMulti?: boolean;
    closeMenuOnSelect?: boolean;
    placeholder?: string;
    error?: string | boolean;
    showError?: boolean;
    searchableLimit: number;
    maxSelectableOptions: number;
    value: SelectValue;
    field_not_same_validation?: boolean;
    field_not_same_source?: string;
    onChange?: (value?: any) => void;
    onBlur?: (value?: any) => void;
    onFocus?: (value?: any) => void;
    onInputChange?: (value: string, actionMeta: InputActionMeta) => void;
    context?: FormContext;
    control?: any;
    singleValue?: any;
    isRequired?: boolean;
    customComponents?: any;
    isLoading?: boolean;
    isSearchable?: boolean;
    isDisabled?: boolean;
    isClearable?: boolean;
    inputValue?: string;
    openMenuOnClick?: boolean;
    "aria-label"?: string;
    styles?: any;
};

const checkMenuPlacement = isMobile() ? "bottom" : "auto";

const Select = ({
    choices,
    id,
    menuIsOpen,
    classNamePrefix,
    isMulti,
    tabIndex,
    closeMenuOnSelect,
    placeholder,
    onChange,
    onBlur,
    onFocus,
    onInputChange,
    searchableLimit,
    maxSelectableOptions,
    value,
    field_not_same_validation,
    context,
    control,
    singleValue,
    cssClass,
    customComponents,
    isLoading,
    isSearchable,
    inputValue,
    isClearable,
    openMenuOnClick,
    ...props
}: Props) => {
    const [isDisabled, setIsDisabled] = useState<boolean>(
        props.isDisabled || false
    );

    // Setting max selectable: https://stackoverflow.com/questions/55119120/how-to-set-max-number-of-items-that-can-be-selected-in-react-select
    const Menu = (props: any) => {
        const optionSelectedLength = props.getValue().length || 0;
        return (
            <components.Menu {...props}>
                {optionSelectedLength < maxSelectableOptions ? (
                    props.children
                ) : (
                    <div>
                        <p className={`u-p-5 u-m-0 u-color-monotoneDark`}>
                            The maximum number of options have been selected.
                        </p>
                    </div>
                )}
            </components.Menu>
        );
    };

    let field_not_same_source;
    try {
        field_not_same_source = JSON.parse(`${props.field_not_same_source}`);
        field_not_same_source = Array.isArray(field_not_same_source)
            ? field_not_same_source
            : [field_not_same_source];
    } catch (error) {
        field_not_same_source = [];
    }
    const excludeValues =
        (context &&
            field_not_same_validation &&
            field_not_same_source.length &&
            field_not_same_source.map(
                (id: string) => (context.getValue(`input_${id}`) || {}).value
            )) ||
        [];
    let optionsLength = 0;
    const options = choices.reduce(
        (o: {
            label: string;
            value?: string;
            options?: { label: string, value: string }[];
            customProps?: any;
        }[], choice: iChoice) => {
            let hidden = false;
            if (context && choice.conditionalLogic) {
                hidden = evaluates_as_hiddenV2(
                    choice.conditionalLogic,
                    context.state
                );
            }
            if (!hidden) {
                let label = choice.text;

                if (choice.value !== undefined && !excludeValues.includes(choice.value)) {
                    if (!stringHasValueTag(choice.value)) {
                        label = replaceMergeTagsInString(
                            choice.text,
                            context ? context.getValues() : {}
                        );
                    }
                    optionsLength++;
    
                    return [
                        ...o,
                        {
                            label: label,
                            value: choice.value,
                            customProps: choice.customProps
                        }
                    ];
                } else if (choice.options !== undefined && choice.options.length) {
                    const subOptions: { label: string, value: string }[] = [];
                    choice.options.forEach(subOption => {
                        subOptions.push({
                            label: subOption.text,
                            value: subOption.value
                        });
                        optionsLength++;
                    });

                    return [
                        ...o,
                        {
                            label: label,
                            options: subOptions
                        }
                    ];
                } else {
                    return o;
                }
            } else {
                return o;
            }
        },
        []
    );

    /**
     * Ensure values exist within the drop down
     */
    let selectedValue: SelectValue | SelectValue[] | null;
    if (isMulti) {
        const requestedValues = Array.isArray(value)
            ? value.map(v => v.value)
            : [value];
        selectedValue = options.filter(v => requestedValues.includes(v.value));
        selectedValue =
            selectedValue !== null && selectedValue.length
                ? selectedValue
                : null;
    } else if (isSearchable && !options.length) {
        selectedValue = value;
    } else {
        const requestedValue =
            value && typeof value === "object" ? value.value : value;
        selectedValue = null;
        for (const option of options) {
            if ((option.value != null) && option.value === requestedValue) {
                selectedValue = option;
            //@ts-ignore
            } else if (option.options && option.options.length) {
                //@ts-ignore
                for (const subOption of option.options) {
                    if (subOption.value === requestedValue) {
                        selectedValue = subOption;
                        break;
                    }
                }
            }
            if (selectedValue) {
                break;
            }
        }
    }

    const unsetValueEffect = (value: SelectValue) => () => {
        if (value && selectedValue === null && props.name) {
            context?.unsetValue(props.name);
        }
    };

    const disableSelect = () => {
        //@ts-ignore
        if (props.isRequired && options.length === 1 && !options[0].options) {
            setIsDisabled(true);
            if (!selectedValue) {
                selectedValue = isMulti ? options : options[0];
                onChange && onChange(selectedValue);
            }
        }
    };

    useEffect(disableSelect, [choices]);
    useEffect(unsetValueEffect(value), [selectedValue]);

    return (
        <Container {...{ ...props, id, cssClass }}>
            <EnchancedSelect
                isSearchable={
                    isSearchable || optionsLength > searchableLimit
                        ? true
                        : false
                }
                defaultMenuIsOpen={menuIsOpen}
                value={selectedValue}
                closeMenuOnSelect={closeMenuOnSelect}
                blurInputOnSelect={false}
                components={{
                    Menu,
                    ...(control && { Control: control }),
                    ...(singleValue && { SingleValue: singleValue }),
                    ...customComponents
                }}
                maxMenuHeight={410}
                tabIndex={tabIndex}
                isMulti={isMulti}
                menuShouldScrollIntoView={true}
                menuPlacement={checkMenuPlacement}
                placeholder={
                    placeholder
                        ? placeholder
                        : optionsLength > searchableLimit
                        ? "Type to search"
                        : "Please select"
                }
                styles={{
                    ...SelectCustomStyles,
                    ...props.styles
                }}
                classNamePrefix={classNamePrefix}
                inputId={id}
                className="react-select-container"
                options={options}
                onChange={(e: any) => onChange && onChange(e)}
                onBlur={(e: any) => onBlur && onBlur(e)}
                onFocus={(e: any) => onFocus && onFocus(e)}
                onInputChange={onInputChange}
                isLoading={isLoading}
                isDisabled={isDisabled}
                inputValue={inputValue}
                isClearable={isClearable}
                openMenuOnClick={openMenuOnClick}
                aria-label={props["aria-label"] ?? ""}
            />
        </Container>
    );
};
Select.defaultProps = {
    choices: [],
    menuIsOpen: false,
    classNamePrefix: "react-select",
    isMulti: false,
    closeMenuOnSelect: true,
    placeholder: "Choose an option ...",
    searchableLimit: 25,
    maxSelectableOptions: 4,
    value: "",
    error: "",
    isLoading: false
};
export default Select;